import {
  PinSvg,
  stripWidthAndHeightFromSvg,
} from '@app/components/Molecules/MapView/RiskMapView/LayerTypes'
import { ElementItem } from '@app/domains/layers/types/layers.types'
import { Map as MapboxMap } from 'mapbox-gl'
import { useEffect, useMemo } from 'react'

/**
 * Single-element renderer for RiskDataDetailPage.
 *
 * It expects an `ElementItem`that contains at least:
 *   - tilesets[0].id
 *   - tilesets[0].sourceLayer
 *   - tilesets[0].type (optional; supports 'icon' vs default fill/line)
 */
export interface ElementLayerProps {
  map: MapboxMap
  element: ElementItem | null
  /** Optional stable id used to namespace source/layer ids. Defaults to element.type */
  elementId?: string
  /** Optional namespace prefix to avoid collisions with other maps */
  namespace?: string
}

export const ElementSingleLayer = ({
  map,
  element,
  elementId,
  namespace = 'detail',
}: ElementLayerProps) => {
  const id = useMemo(() => {
    if (!element) return null
    return elementId ?? element.type
  }, [element, elementId])

  useEffect(() => {
    // Always clear previously-rendered layers for this component
    clearElementLayers(map, namespace)

    if (!element || !id) return

    addElementToMap(map, element, id, namespace)

    return () => {
      clearElementLayers(map, namespace)
    }
  }, [map, element, id, namespace])

  return null
}

/**
 * Add a single element layer to the map.
 * Styling mirrors the multi-layer version (feature-state driven colors).
 */
const PUBLIC_ASSETS_BASE_URL = import.meta.env.VITE_PUBLIC_ASSETS_BASE_URL

function addElementToMap(
  map: MapboxMap,
  element: ElementItem,
  elementId: string,
  namespace: string,
): string | null {
  const sourceId = `${namespace}-element-source-${elementId}`
  const layerId = `${namespace}-element-layer-${elementId}`

  if (!element.tilesets || element.tilesets.length === 0) {
    return null
  }

  const tileset = element.tilesets[0]
  const tileUrl = `mapbox://${tileset.id}`

  // Insert below labels for readability
  const beforeId = map.getStyle().layers?.find((l) => l.type === 'symbol')?.id

  try {
    // Add source (vector)
    if (!map.getSource(sourceId)) {
      map.addSource(sourceId, {
        type: 'vector',
        url: tileUrl,
        // If the tileset has feature ids, promote them so feature-state works
        promoteId: 'id',
      })
    }

    // Icon tileset -> symbol layer
    if (tileset.type === 'icon') {
      if (!map.getLayer(layerId)) {
        const iconSrc = tileset.icon ?? element.icon
        if (!iconSrc) {
          console.warn(`Element ${elementId} has icon tileset type but no icon URL`, element)
          return null
        }

        const iconId = String(iconSrc).toLowerCase().replace('s3://', '').replace('.svg', '')
        const imageId = `${namespace}-${iconId}-default`
        const iconUrl = `${PUBLIC_ASSETS_BASE_URL}/icons/${iconId}.svg`

        const addSymbolLayer = () => {
          if (map.getLayer(layerId)) return
          map.addLayer(
            {
              id: layerId,
              type: 'symbol',
              source: sourceId,
              'source-layer': tileset.sourceLayer,
              layout: {
                'icon-image': imageId,
                'icon-size': ['interpolate', ['linear'], ['zoom'], 10, 0.33, 18, 1.125],
                'icon-allow-overlap': true,
                'icon-anchor': 'bottom',
                visibility: 'visible',
              },
              paint: {
                'icon-opacity': 1,
              },
              minzoom: 1,
            },
            beforeId,
          )
        }

        if (map.hasImage(imageId)) {
          addSymbolLayer()
        } else {
          fetch(iconUrl)
            .then((res) => res.text())
            .then((iconSvg) => {
              const stripped = stripWidthAndHeightFromSvg(iconSvg)
              const pinWithIcon = PinSvg.replace('<!--ICONSVG-->', stripped)
              const svgBitmap = new Image(42, 51)
              svgBitmap.onload = () => {
                if (!map.hasImage(imageId)) {
                  map.addImage(imageId, svgBitmap)
                }
                addSymbolLayer()
              }
              svgBitmap.src =
                'data:image/svg+xml;base64,' + btoa(pinWithIcon.replace('#777777', '#0b2a48'))
            })
            .catch((err) => {
              console.error(`Failed to load icon for element ${elementId}:`, { iconUrl, err })
            })
        }
      }

      return layerId
    }

    // Default: fill + outline layers
    if (!map.getLayer(layerId)) {
      map.addLayer(
        {
          id: layerId,
          type: 'fill',
          source: sourceId,
          'source-layer': tileset.sourceLayer,
          paint: {
            'fill-color': [
              'case',
              ['boolean', ['feature-state', 'selected'], false],
              '#ef4444',
              ['boolean', ['feature-state', 'hover'], false],
              '#0b2948',
              '#0b2948',
            ],
            'fill-opacity': 1,
          },
        },
        beforeId,
      )

      const outlineLayerId = `${layerId}-outline`
      map.addLayer(
        {
          id: outlineLayerId,
          type: 'line',
          source: sourceId,
          'source-layer': tileset.sourceLayer,
          paint: {
            'line-color': [
              'case',
              ['boolean', ['feature-state', 'selected'], false],
              '#dc2626',
              ['boolean', ['feature-state', 'hover'], false],
              '#2563eb',
              '#64748b',
            ],
            'line-width': [
              'case',
              ['boolean', ['feature-state', 'selected'], false],
              3,
              ['boolean', ['feature-state', 'hover'], false],
              2,
              1.25,
            ],
          },
        },
        beforeId,
      )
    }

    return layerId
  } catch (error) {
    console.error(`Failed to add element ${elementId}:`, error)
    return null
  }
}

/**
 * Remove all layers/sources created by ElementLayer for a given namespace.
 */
function clearElementLayers(map: MapboxMap, namespace: string) {
  const style = map.getStyle()
  if (!style) return

  const prefix = `${namespace}-element-layer-`

  const elementLayers = style.layers?.filter((layer) => layer.id.startsWith(prefix)) || []

  elementLayers.forEach((layer) => {
    if (map.getLayer(layer.id)) {
      map.removeLayer(layer.id)
    }

    const outlineId = `${layer.id}-outline`
    if (map.getLayer(outlineId)) {
      map.removeLayer(outlineId)
    }
  })

  const sourcePrefix = `${namespace}-element-source-`
  const elementSources = Object.keys(style.sources || {}).filter((sourceId) =>
    sourceId.startsWith(sourcePrefix),
  )

  elementSources.forEach((sourceId) => {
    if (map.getSource(sourceId)) {
      map.removeSource(sourceId)
    }
  })
}
